import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/forms/Buttons/Button";
import { postTeamMember } from "../../../services/olympiad/teamService";
import TeamHeader from "./TeamHeader";
import MemberList from "./MemberList";
import AddMemberForm from "./AddMemberForm";
import {
  deleteTeamMember,
  patchTeamMember,
} from "../../../services/olympiad/teamMemberService";
import { UserContext } from "../../../utils/contexts/UserContext";
import { PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import RulesOfTeam from "../RulesOfTeam";

export default function TeamMembers({ team, setTeam }) {
  const { user } = useContext(UserContext);
  const [t] = useTranslation();
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const maxMembers = team?.max_participant_limit;
  const participantMembers = team?.team_members?.filter(
    (member) => member.role !== "SUPERVISOR"
  );
  const canAddMember = participantMembers?.length < maxMembers;

  const getErrorMessage = (errorCode) => {
    const errorMap = {
      1: t("team.error_permission_denied"),
      2: t("team.error_missing_fields"),
      3: t("team.error_invalid_username"),
      4: t("team.error_invalid_grade"),
      5: t("team.error_invalid_language"),
      6: t("team.error_email_taken"),
      7: t("team.error_iin_taken"),
      9: t("team.error_team_limit_reached"),
      10: t("team.error_account_creation_failed"),
      11: t("team.error_team_member_creation_failed"),
    };
    return errorMap[errorCode] || t("team.error_team_member_creation_failed");
  };

  const onSubmitMember = (data) => {
    setLoading(true);
    setError(null);
    const memberData = {
      first_name: data.firstName,
      last_name: data.lastName,
      middle_name: data.middleName,
      email: data.email,
      iin: data.memberIin,
      // language: data.language,
      grade: data.grade,
      gender: data.gender,
      phone_number: data.phone_number ? `+7${data.phone_number}` : undefined,
    };

    const promise = editingMember
      ? patchTeamMember(editingMember.uuid, memberData)
      : postTeamMember(team.uuid, memberData);

    promise
      .then((response) => {
        if (editingMember) {
          // Update existing member
          setTeam((prevTeam) => ({
            ...prevTeam,
            team_members: prevTeam.team_members.map((m) =>
              m.uuid === editingMember.uuid ? response : m
            ),
          }));
          setEditingMember(null);
          toast.success(t("team.member_updated"));
        } else {
          // Add new member
          setTeam((prevTeam) => ({
            ...prevTeam,
            team_members: [...prevTeam.team_members, response],
          }));
          setShowAddMember(false);
          toast.success(t("team.member_added"));
        }
      })
      .catch((err) => {
        console.error(
          editingMember ? "Error updating member:" : "Error adding member:",
          err
        );
        if (editingMember) {
          let errorHandled = false;
          if (err.response.data.email?.code === "ALREADY_EXISTS") {
            errorHandled = true;
            setError(t("signup.email_is_taken"));
            toast.error(t("signup.email_is_taken"));
          }
          if (err.response.data.username?.code === "ALREADY_EXISTS") {
            errorHandled = true;
            setError(t("signup.iin_is_taken"));
            toast.error(t("signup.iin_is_taken"));
          }
          if (!errorHandled) {
            setError("Something went wrong");
            toast.error("Something went wrong");
            console.log(err.response.data);
          }
        } else {
          const errorCode = err.response?.data?.error_code;
          const errorMessage = errorCode
            ? getErrorMessage(errorCode)
            : err.response?.data?.detail ||
              (editingMember
                ? t("team.error_team_member_update_failed")
                : t("team.error_team_member_creation_failed"));
          setError(errorMessage);
          toast.error(errorMessage);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRemoveMember = (memberUuid) => {
    return new Promise((resolve, reject) => {
      deleteTeamMember(memberUuid)
        .then((res) => {
          console.log(res);
          const updatedTeam = {
            ...team,
            team_members: team.team_members.filter(
              (m) => m.uuid !== memberUuid
            ),
          };
          setTeam(updatedTeam);
          resolve(res);
        })
        .catch((err) => {
          console.error("Error removing member:", err);
          reject(err);
        });
    });
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setShowAddMember(false);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setShowAddMember(false);
    setError(null);
  };

  const isSupervisor =
    team?.team_members?.find((member) => member.role === "SUPERVISOR")?.user
      ?.uuid === user?.uuid &&
    user.roles.some((role) => role.code === "SUPERVISOR");

  return (
    <div className=" rounded-lg border border-gray-300 p-8">
      <TeamHeader
        team={team}
        onTeamUpdate={setTeam}
        isSupervisor={isSupervisor}
      />

      {team.status === "DRAFT" && isSupervisor && <RulesOfTeam />}

      <MemberList
        members={participantMembers}
        onRemoveMember={handleRemoveMember}
        onEditMember={handleEditMember}
        canEdit={team.status === "DRAFT" && isSupervisor}
        maxMembers={maxMembers}
        setTeam={setTeam}
        isSupervisor={isSupervisor}
      />

      {/* Add Member Button */}
      {team.status === "DRAFT" &&
        isSupervisor &&
        canAddMember &&
        !showAddMember &&
        !editingMember && (
          <div className="flex flex-row justify-end w-full">
            <Button
              className="w-full lg:w-auto"
              text={t("team.add_member")}
              color="primary"
              Icon={PlusIcon}
              onClick={() => setShowAddMember(true)}
            />
          </div>
        )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Add/Edit Member Form */}
      {team.status === "DRAFT" &&
        isSupervisor &&
        (showAddMember || editingMember) && (
          <AddMemberForm
            currentMembers={participantMembers}
            onSubmit={onSubmitMember}
            onCancel={handleCancelEdit}
            loading={loading}
            initialData={editingMember}
            isEditMode={!!editingMember}
          />
        )}

      {!canAddMember && team.status !== "DRAFT" && isSupervisor && (
        <div className="bg-primary bg-opacity-10 border border-primary/10 rounded-md p-4">
          <p className="text-sm text-primary">
            {t("team.max_members_reached")}
          </p>
        </div>
      )}
    </div>
  );
}
